import axios from 'axios';

// Use the staging API base URL
const API_BASE_URL = 'https://dactrha-staging.novech-sy.com/api';

// Create axios instance for clinic management
const clinicApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Add auth token to requests
clinicApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ============================================
// Dashboard API - Specialty-specific metrics
// ============================================
export const dashboardApi = {
  // Get complete dashboard summary for authenticated doctor
  getSummary: () => clinicApi.get('/doctor/dashboard/summary'),
  
  // Get KPI cards data
  getKPICards: () => clinicApi.get('/doctor/dashboard/kpi'),
  
  // Get chart data
  getCharts: () => clinicApi.get('/doctor/dashboard/charts'),
  
  // Get alerts
  getAlerts: () => clinicApi.get('/doctor/dashboard/alerts'),
  
  // Get dashboard for specific specialty
  getBySpecialty: (specialty) => clinicApi.get(`/doctor/dashboard/specialty/${specialty}`),
  
  // Get available specialties
  getAvailableSpecialties: () => clinicApi.get('/doctor/dashboard/available-specialties'),
};

// ============================================
// Clinic Management API
// ============================================
export const clinicManagementApi = {
  // Get all clinics for clinic owner
  getClinics: () => clinicApi.get('/clinic_owner/clinics'),
  
  // Get single clinic
  getClinic: (id) => clinicApi.get(`/clinic_owner/clinics/${id}`),
  
  // Create clinic
  createClinic: (data) => clinicApi.post('/clinics', data),
  
  // Update clinic
  updateClinic: (id, data) => clinicApi.put(`/clinics/${id}`, data),
  
  // Delete clinic
  deleteClinic: (id) => clinicApi.delete(`/clinics/${id}`),
  
  // Get clinic statistics
  getStatistics: (clinicId) => clinicApi.get(`/clinics/${clinicId}/statistics`),
};

// ============================================
// Staff Management API
// ============================================
export const staffApi = {
  // Get all employees
  getEmployees: () => clinicApi.get('/clinic_owner/employees'),
  
  // Get doctors
  getDoctors: () => clinicApi.get('/clinic_owner/doctors'),
  
  // Get doctor by ID
  getDoctor: (id) => clinicApi.get(`/doctors/${id}`),
  
  // Update availability hours
  updateAvailability: (id, data) => clinicApi.put(`/availability_hours/${id}`, data),
  
  // Create availability hours
  createAvailability: (data) => clinicApi.post('/availability_hours', data),
  
  // Delete availability hours
  deleteAvailability: (id) => clinicApi.delete(`/availability_hours/${id}`),
  
  // Get awards
  getAwards: () => clinicApi.get('/awards'),
  
  // Create award
  createAward: (data) => clinicApi.post('/awards', data),
  
  // Update award
  updateAward: (id, data) => clinicApi.put(`/awards/${id}`, data),
  
  // Delete award
  deleteAward: (id) => clinicApi.delete(`/awards/${id}`),
  
  // Get experiences
  getExperiences: () => clinicApi.get('/experiences'),
  
  // Create experience
  createExperience: (data) => clinicApi.post('/experiences', data),
  
  // Update experience
  updateExperience: (id, data) => clinicApi.put(`/experiences/${id}`, data),
  
  // Delete experience
  deleteExperience: (id) => clinicApi.delete(`/experiences/${id}`),
};

// ============================================
// Patient Management API
// ============================================
export const patientApi = {
  // Get all patients
  getPatients: (params) => clinicApi.get('/patients', { params }),
  
  // Get patient by ID
  getPatient: (id) => clinicApi.get(`/patients/${id}`),
  
  // Create patient
  createPatient: (data) => clinicApi.post('/patients', data),
  
  // Update patient
  updatePatient: (id, data) => clinicApi.post(`/patients/${id}`, data),
  
  // Delete patient
  deletePatient: (id) => clinicApi.delete(`/patients/${id}`),
  
  // Get patient medical history
  getMedicalHistory: (userId) => clinicApi.get(`/medical-history/${userId}`),
  
  // Update medical history
  updateMedicalHistory: (id, data) => clinicApi.post(`/medical-history/${id}`, data),
  
  // Create medical history
  createMedicalHistory: (data) => clinicApi.post('/create-medical-history', data),
  
  // Get doctor's patients
  getDoctorPatients: () => clinicApi.get('/doctor/patients'),
};

// ============================================
// Appointments API
// ============================================
export const appointmentsApi = {
  // Get all appointments
  getAll: (params) => clinicApi.get('/appointments', { params }),
  
  // Get appointment by ID
  getById: (id) => clinicApi.get(`/appointments/${id}`),
  
  // Create appointment
  create: (data) => clinicApi.post('/appointments', data),
  
  // Update appointment
  update: (id, data) => clinicApi.put(`/appointments/${id}`, data),
  
  // Delete appointment
  delete: (id) => clinicApi.delete(`/appointments/${id}`),
  
  // Get appointment statuses
  getStatuses: () => clinicApi.get('/appointment_statuses'),
  
  // Get assistant appointments
  getAssistantAppointments: () => clinicApi.get('/appointments/assistant'),
};

// ============================================
// Billing & Payments API
// ============================================
export const billingApi = {
  // Get payments by month
  getPaymentsByMonth: () => clinicApi.get('/payments/byMonth'),
  
  // Process wallet payment
  processWalletPayment: (id, data) => clinicApi.post(`/payments/wallets/${id}`, data),
  
  // Process cash payment
  processCashPayment: (data) => clinicApi.post('/payments/cash', data),
  
  // Get payment methods
  getPaymentMethods: () => clinicApi.get('/payment_methods'),
  
  // Get wallets
  getWallets: () => clinicApi.get('/wallets'),
  
  // Create wallet
  createWallet: (data) => clinicApi.post('/wallets', data),
  
  // Update wallet
  updateWallet: (id, data) => clinicApi.put(`/wallets/${id}`, data),
  
  // Get wallet transactions
  getTransactions: () => clinicApi.get('/wallet_transactions'),
  
  // Get earnings
  getEarnings: () => clinicApi.get('/earnings'),
  
  // Get clinic payouts
  getClinicPayouts: () => clinicApi.get('/clinic_payouts'),
};

// ============================================
// Consultation API (Medical Records)
// ============================================
export const consultationApi = {
  // Get all consultations
  getAll: () => clinicApi.get('/doctor/consultations'),
  
  // Get consultation by ID
  getById: (id) => clinicApi.get(`/doctor/consultations/${id}`),
  
  // Create consultation
  create: (data) => clinicApi.post('/doctor/consultations', data),
  
  // Update consultation
  update: (id, data) => clinicApi.post(`/doctor/consultations/${id}`, data),
  
  // Delete consultation
  delete: (id) => clinicApi.delete(`/doctor/consultations/${id}`),
  
  // Get patient consultations
  getPatientConsultations: (patientId) => clinicApi.get(`/doctor/show-patient-consultations/${patientId}`),
};

// ============================================
// Medical History API
// ============================================
export const medicalHistoryApi = {
  // Get chronic diseases
  getChronicDiseases: () => clinicApi.get('/chronic-diseases'),
  
  // Get permanent medications
  getPermanentMedications: () => clinicApi.get('/permanent-medications'),
  
  // Get genetic diseases
  getGeneticDiseases: () => clinicApi.get('/genetic-diseases'),
  
  // Get surgeries
  getSurgeries: () => clinicApi.get('/surgeries'),
  
  // Get vaccines
  getVaccines: () => clinicApi.get('/vaccines'),
  
  // Get all medical history data
  getAll: () => clinicApi.get('/all'),
};

// ============================================
// Tenant/Multi-Branch API
// ============================================
export const tenantApi = {
  // Ping tenant
  ping: () => clinicApi.get('/tenant/ping'),
  
  // Get current tenant
  getCurrent: () => clinicApi.get('/tenant/current'),
  
  // Get all tenants
  getAll: () => clinicApi.get('/tenants'),
  
  // Get tenant by ID
  getById: (id) => clinicApi.get(`/tenants/${id}`),
  
  // Create tenant
  create: (data) => clinicApi.post('/tenants', data),
  
  // Update tenant
  update: (id, data) => clinicApi.put(`/tenants/${id}`, data),
  
  // Delete tenant
  delete: (id) => clinicApi.delete(`/tenants/${id}`),
  
  // Get tenant statistics
  getStatistics: (id) => clinicApi.get(`/tenants/${id}/statistics`),
};

// ============================================
// Reviews API
// ============================================
export const reviewsApi = {
  // Get doctor reviews
  getDoctorReviews: (doctorId) => clinicApi.get(`/doctor_reviews/${doctorId}`),
  
  // Create doctor review
  createDoctorReview: (data) => clinicApi.post('/doctor_reviews', data),
  
  // Get clinic reviews
  getClinicReviews: (clinicId) => clinicApi.get(`/clinic_reviews/${clinicId}`),
  
  // Create clinic review
  createClinicReview: (data) => clinicApi.post('/clinic_reviews', data),
  
  // Get predefined reviews
  getPredefinedReviews: () => clinicApi.get('/predefined_reviews'),
};

// ============================================
// Insurance API
// ============================================
export const insuranceApi = {
  // Get insurance companies
  getCompanies: () => clinicApi.get('/insurance-companies'),
};

// ============================================
// Notifications API
// ============================================
export const notificationsApi = {
  // Get all notifications
  getAll: () => clinicApi.get('/notifications'),
  
  // Get notification count
  getCount: () => clinicApi.get('/notifications/count'),
  
  // Mark notification as read
  markAsRead: (id) => clinicApi.put(`/notifications/${id}`),
};

// ============================================
// Upload API
// ============================================
export const uploadApi = {
  // Upload file
  upload: (formData) => clinicApi.post('/uploads/store', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  
  // Clear uploads
  clear: () => clinicApi.post('/uploads/clear'),
};

// ============================================
// Specialty API
// ============================================
export const specialtyApi = {
  // Get all specialties
  getAll: () => clinicApi.get('/specialities'),
  
  // Get specialty by ID
  getById: (id) => clinicApi.get(`/specialities/${id}`),
};

// Helper function to get localized value
export const getLocalizedValue = (obj, lang = 'ar') => {
  if (!obj) return '';
  if (typeof obj === 'string') return obj;
  return obj[lang] || obj.ar || obj.en || '';
};

export default clinicApi;
